/// Geodesy helpers ported from Swift.
/// See FlatoutATC/Models/Models.swift:472-492 (GeoCoordinate.distance(to:)
/// and bearing(to:)).
///
/// `bearing` returns TRUE bearing in degrees [0, 360). The iOS code
/// subtracts magvar where it needs magnetic; we do the same when we
/// compare against pilot-issued (magnetic) headings.

import type { GeoCoordinate } from '../types/LevelData'

const EARTH_RADIUS_M = 6_372_797.560856 // CLLocation uses WGS84-ish; this matches close enough

/// Great-circle distance in meters.
export function distanceMeters(a: GeoCoordinate, b: GeoCoordinate): number {
  const lat1 = (a.latitude * Math.PI) / 180
  const lat2 = (b.latitude * Math.PI) / 180
  const dLat = lat2 - lat1
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180

  const sinDLat = Math.sin(dLat / 2)
  const sinDLon = Math.sin(dLon / 2)
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
  return EARTH_RADIUS_M * c
}

/// Distance in nautical miles.
export function distanceNM(a: GeoCoordinate, b: GeoCoordinate): number {
  return distanceMeters(a, b) / 1852
}

/// True bearing from `from` to `to`, in degrees [0, 360).
/// Direct port of Swift `bearing(to:)`.
export function bearingDegrees(from: GeoCoordinate, to: GeoCoordinate): number {
  const lat1 = (from.latitude * Math.PI) / 180
  const lon1 = (from.longitude * Math.PI) / 180
  const lat2 = (to.latitude * Math.PI) / 180
  const lon2 = (to.longitude * Math.PI) / 180

  const dLon = lon2 - lon1
  const y = Math.sin(dLon) * Math.cos(lat2)
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)

  let deg = (Math.atan2(y, x) * 180) / Math.PI
  if (deg < 0) deg += 360
  return deg
}
